/**
 * User Config Hook
 * Syncs user configuration with KV backend
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from './useAuth';

export interface UserConfig {
  queryHistory?: Array<{
    id: string;
    query: string;
    timestamp: number;
    starred?: boolean;
  }>;
  dateRange?: {
    preset: string;
    startDate: string | null;
    endDate: string | null;
  };
  preferences?: {
    theme?: 'light' | 'dark';
    sidebarOpen?: boolean;
  };
}

interface UseUserConfigOptions {
  onConfigLoaded?: (config: UserConfig) => void;
}

export function useUserConfig(options: UseUserConfigOptions = {}) {
  const { accessToken, isAuthenticated } = useAuth();
  const { onConfigLoaded } = options;
  const loadedRef = useRef(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Fetch config from backend
   */
  const fetchConfig = useCallback(async (): Promise<UserConfig | null> => {
    if (!accessToken) return null;

    try {
      const response = await fetch('/api/user/config', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        console.warn('[useUserConfig] Failed to fetch config:', response.status);
        return null;
      }

      const data = await response.json() as { config?: UserConfig };
      return data.config || null;
    } catch (error) {
      console.error('[useUserConfig] Fetch error:', error);
      return null;
    }
  }, [accessToken]);

  /**
   * Save config to backend (debounced)
   */
  const saveConfig = useCallback(async (config: Partial<UserConfig>) => {
    if (!accessToken) return;

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce saves by 1 second
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch('/api/user/config', {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ config }),
        });

        if (!response.ok) {
          console.warn('[useUserConfig] Failed to save config:', response.status);
        }
      } catch (error) {
        console.error('[useUserConfig] Save error:', error);
      }
    }, 1000);
  }, [accessToken]);

  /**
   * Load config on mount when authenticated
   */
  useEffect(() => {
    if (isAuthenticated && !loadedRef.current) {
      loadedRef.current = true;
      fetchConfig().then((config) => {
        if (config && onConfigLoaded) {
          onConfigLoaded(config);
        }
      });
    }
  }, [isAuthenticated, fetchConfig, onConfigLoaded]);

  /**
   * Cleanup pending saves on unmount
   */
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    fetchConfig,
    saveConfig,
  };
}
